// Artifact viewers: chrome around a deck, document, spreadsheet, report and page
// (T1-UI1). Namespace `artifact-viewers`.
//
// Chrome only. Artifact content, narration and numbers come from the artifact
// itself and are never written here. The word for the generic noun is one key.
// All 24 app languages are written out below: `en` and `sv` are the source, the rest are
// translated chrome copy (placeholders and the product word "Artifact" preserved).
// See ./index.ts.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  // Present mode
  presenting: 'Presenting',
  stopPresenting: 'Stop presenting',
  previousSlide: 'Previous slide',
  nextSlide: 'Next slide',
  slideOf: 'Slide {at} of {count}',
  showNotes: 'Speaker notes',
  hideNotes: 'Hide speaker notes',
  notesPanel: 'Speaker notes for this slide',
  noNotes: 'No speaker notes on this slide.',
  presentHint: 'Arrow keys move, N toggles notes, Esc stops.',
  moveHint: '← → to move · Esc to stop',
  notesLabel: 'Speaker notes · only you see these',
};

const sv: typeof en = {
  presenting: 'Presenterar',
  stopPresenting: 'Sluta presentera',
  previousSlide: 'Föregående bild',
  nextSlide: 'Nästa bild',
  slideOf: 'Bild {at} av {count}',
  showNotes: 'Talaranteckningar',
  hideNotes: 'Dölj talaranteckningar',
  notesPanel: 'Talaranteckningar för den här bilden',
  noNotes: 'Inga talaranteckningar på den här bilden.',
  presentHint: 'Piltangenterna flyttar, N visar anteckningar, Esc avslutar.',
  moveHint: '← → för att flytta · Esc för att sluta',
  notesLabel: 'Talaranteckningar · bara du ser dem',
};

type Key = keyof typeof en;

export type ArtifactViewersCopy = Readonly<Record<Key, string>>;

const translated: Omit<NamespaceCatalog<Key>, 'en' | 'sv'> = {
  // Bulgarian
  bg: {
    presenting: 'Представяне',
    stopPresenting: 'Спиране на презентацията',
    previousSlide: 'Предишен слайд',
    nextSlide: 'Следващ слайд',
    slideOf: 'Слайд {at} от {count}',
    showNotes: 'Бележки на лектора',
    hideNotes: 'Скриване на бележките на лектора',
    notesPanel: 'Бележки на лектора за този слайд',
    noNotes: 'Този слайд няма бележки на лектора.',
    presentHint: 'Стрелките сменят слайда, N показва бележките, Esc спира.',
    moveHint: '← → за придвижване · Esc за спиране',
    notesLabel: 'Бележки на лектора · виждате ги само вие',
  },
  // Czech
  cs: {
    presenting: 'Prezentace',
    stopPresenting: 'Zastavit prezentaci',
    previousSlide: 'Předchozí snímek',
    nextSlide: 'Další snímek',
    slideOf: 'Snímek {at} z {count}',
    showNotes: 'Poznámky řečníka',
    hideNotes: 'Skrýt poznámky řečníka',
    notesPanel: 'Poznámky řečníka k tomuto snímku',
    noNotes: 'Tento snímek nemá poznámky řečníka.',
    presentHint: 'Šipky přepínají snímky, N zobrazí poznámky, Esc zastaví.',
    moveHint: '← → pro přechod · Esc pro zastavení',
    notesLabel: 'Poznámky řečníka · vidíte je jen vy',
  },
  // Danish
  da: {
    presenting: 'Præsenterer',
    stopPresenting: 'Stop præsentationen',
    previousSlide: 'Forrige slide',
    nextSlide: 'Næste slide',
    slideOf: 'Slide {at} af {count}',
    showNotes: 'Talernoter',
    hideNotes: 'Skjul talernoter',
    notesPanel: 'Talernoter til dette slide',
    noNotes: 'Ingen talernoter på dette slide.',
    presentHint: 'Piletasterne skifter slide, N viser noter, Esc stopper.',
    moveHint: '← → for at bladre · Esc for at stoppe',
    notesLabel: 'Talernoter · kun du kan se dem',
  },
  // German
  de: {
    presenting: 'Präsentation läuft',
    stopPresenting: 'Präsentation beenden',
    previousSlide: 'Vorherige Folie',
    nextSlide: 'Nächste Folie',
    slideOf: 'Folie {at} von {count}',
    showNotes: 'Sprechernotizen',
    hideNotes: 'Sprechernotizen ausblenden',
    notesPanel: 'Sprechernotizen zu dieser Folie',
    noNotes: 'Keine Sprechernotizen auf dieser Folie.',
    presentHint: 'Pfeiltasten wechseln die Folie, N blendet Notizen ein, Esc beendet.',
    moveHint: '← → zum Blättern · Esc zum Beenden',
    notesLabel: 'Sprechernotizen · nur für Sie sichtbar',
  },
  // Greek
  el: {
    presenting: 'Παρουσίαση σε εξέλιξη',
    stopPresenting: 'Διακοπή παρουσίασης',
    previousSlide: 'Προηγούμενη διαφάνεια',
    nextSlide: 'Επόμενη διαφάνεια',
    slideOf: 'Διαφάνεια {at} από {count}',
    showNotes: 'Σημειώσεις ομιλητή',
    hideNotes: 'Απόκρυψη σημειώσεων ομιλητή',
    notesPanel: 'Σημειώσεις ομιλητή για αυτή τη διαφάνεια',
    noNotes: 'Αυτή η διαφάνεια δεν έχει σημειώσεις ομιλητή.',
    presentHint: 'Τα βέλη αλλάζουν διαφάνεια, το N εμφανίζει τις σημειώσεις, το Esc σταματά.',
    moveHint: '← → για μετακίνηση · Esc για διακοπή',
    notesLabel: 'Σημειώσεις ομιλητή · τις βλέπετε μόνο εσείς',
  },
  // Spanish
  es: {
    presenting: 'Presentando',
    stopPresenting: 'Detener la presentación',
    previousSlide: 'Diapositiva anterior',
    nextSlide: 'Diapositiva siguiente',
    slideOf: 'Diapositiva {at} de {count}',
    showNotes: 'Notas del orador',
    hideNotes: 'Ocultar las notas del orador',
    notesPanel: 'Notas del orador de esta diapositiva',
    noNotes: 'No hay notas del orador en esta diapositiva.',
    presentHint: 'Las flechas cambian de diapositiva, N muestra las notas y Esc detiene.',
    moveHint: '← → para moverte · Esc para detener',
    notesLabel: 'Notas del orador · solo tú las ves',
  },
  // Estonian
  et: {
    presenting: 'Esitamine',
    stopPresenting: 'Peata esitlus',
    previousSlide: 'Eelmine slaid',
    nextSlide: 'Järgmine slaid',
    slideOf: 'Slaid {at} / {count}',
    showNotes: 'Esineja märkmed',
    hideNotes: 'Peida esineja märkmed',
    notesPanel: 'Esineja märkmed selle slaidi kohta',
    noNotes: 'Sellel slaidil pole esineja märkmeid.',
    presentHint: 'Nooled vahetavad slaidi, N näitab märkmeid, Esc peatab.',
    moveHint: '← → liikumiseks · Esc peatamiseks',
    notesLabel: 'Esineja märkmed · näed neid ainult sina',
  },
  // Finnish
  fi: {
    presenting: 'Esitetään',
    stopPresenting: 'Lopeta esitys',
    previousSlide: 'Edellinen dia',
    nextSlide: 'Seuraava dia',
    slideOf: 'Dia {at} / {count}',
    showNotes: 'Puhujan muistiinpanot',
    hideNotes: 'Piilota puhujan muistiinpanot',
    notesPanel: 'Puhujan muistiinpanot tälle dialle',
    noNotes: 'Tällä dialla ei ole puhujan muistiinpanoja.',
    presentHint: 'Nuolinäppäimet vaihtavat diaa, N näyttää muistiinpanot, Esc lopettaa.',
    moveHint: '← → siirtyäksesi · Esc lopettaaksesi',
    notesLabel: 'Puhujan muistiinpanot · vain sinä näet nämä',
  },
  // French
  fr: {
    presenting: 'Présentation en cours',
    stopPresenting: 'Arrêter la présentation',
    previousSlide: 'Diapositive précédente',
    nextSlide: 'Diapositive suivante',
    slideOf: 'Diapositive {at} sur {count}',
    showNotes: 'Notes du présentateur',
    hideNotes: 'Masquer les notes du présentateur',
    notesPanel: 'Notes du présentateur pour cette diapositive',
    noNotes: 'Aucune note du présentateur sur cette diapositive.',
    presentHint: 'Les flèches changent de diapositive, N affiche les notes, Échap arrête.',
    moveHint: '← → pour naviguer · Échap pour arrêter',
    notesLabel: 'Notes du présentateur · visibles par vous seul',
  },
  // Irish
  ga: {
    presenting: 'Ag cur i láthair',
    stopPresenting: 'Stop an cur i láthair',
    previousSlide: 'An sleamhnán roimhe',
    nextSlide: 'An chéad sleamhnán eile',
    slideOf: 'Sleamhnán {at} as {count}',
    showNotes: 'Nótaí an chainteora',
    hideNotes: 'Folaigh nótaí an chainteora',
    notesPanel: 'Nótaí an chainteora don sleamhnán seo',
    noNotes: 'Níl aon nótaí cainteora ar an sleamhnán seo.',
    presentHint: 'Athraíonn na saigheada an sleamhnán, taispeánann N na nótaí, stopann Esc.',
    moveHint: '← → chun bogadh · Esc chun stopadh',
    notesLabel: 'Nótaí an chainteora · ní fheiceann ach tusa iad',
  },
  // Croatian
  hr: {
    presenting: 'Prezentiranje',
    stopPresenting: 'Zaustavi prezentaciju',
    previousSlide: 'Prethodni slajd',
    nextSlide: 'Sljedeći slajd',
    slideOf: 'Slajd {at} od {count}',
    showNotes: 'Bilješke govornika',
    hideNotes: 'Sakrij bilješke govornika',
    notesPanel: 'Bilješke govornika za ovaj slajd',
    noNotes: 'Ovaj slajd nema bilješki govornika.',
    presentHint: 'Strelice mijenjaju slajd, N prikazuje bilješke, Esc zaustavlja.',
    moveHint: '← → za pomicanje · Esc za zaustavljanje',
    notesLabel: 'Bilješke govornika · vidite ih samo vi',
  },
  // Hungarian
  hu: {
    presenting: 'Előadás folyamatban',
    stopPresenting: 'Előadás leállítása',
    previousSlide: 'Előző dia',
    nextSlide: 'Következő dia',
    slideOf: '{at}. dia / {count}',
    showNotes: 'Előadói jegyzetek',
    hideNotes: 'Előadói jegyzetek elrejtése',
    notesPanel: 'Előadói jegyzetek ehhez a diához',
    noNotes: 'Ehhez a diához nincsenek előadói jegyzetek.',
    presentHint: 'A nyilak diát váltanak, az N megjeleníti a jegyzeteket, az Esc leállít.',
    moveHint: '← → a lapozáshoz · Esc a leállításhoz',
    notesLabel: 'Előadói jegyzetek · csak te látod',
  },
  // Italian
  it: {
    presenting: 'Presentazione in corso',
    stopPresenting: 'Interrompi la presentazione',
    previousSlide: 'Diapositiva precedente',
    nextSlide: 'Diapositiva successiva',
    slideOf: 'Diapositiva {at} di {count}',
    showNotes: 'Note del relatore',
    hideNotes: 'Nascondi le note del relatore',
    notesPanel: 'Note del relatore per questa diapositiva',
    noNotes: 'Nessuna nota del relatore in questa diapositiva.',
    presentHint: 'Le frecce cambiano diapositiva, N mostra le note, Esc interrompe.',
    moveHint: '← → per spostarti · Esc per interrompere',
    notesLabel: 'Note del relatore · le vedi solo tu',
  },
  // Lithuanian
  lt: {
    presenting: 'Pristatoma',
    stopPresenting: 'Sustabdyti pristatymą',
    previousSlide: 'Ankstesnė skaidrė',
    nextSlide: 'Kita skaidrė',
    slideOf: 'Skaidrė {at} iš {count}',
    showNotes: 'Kalbėtojo užrašai',
    hideNotes: 'Slėpti kalbėtojo užrašus',
    notesPanel: 'Kalbėtojo užrašai šiai skaidrei',
    noNotes: 'Šioje skaidrėje nėra kalbėtojo užrašų.',
    presentHint: 'Rodyklės keičia skaidrę, N rodo užrašus, Esc sustabdo.',
    moveHint: '← → norint judėti · Esc norint sustabdyti',
    notesLabel: 'Kalbėtojo užrašai · matote tik jūs',
  },
  // Latvian
  lv: {
    presenting: 'Notiek prezentēšana',
    stopPresenting: 'Apturēt prezentāciju',
    previousSlide: 'Iepriekšējais slaids',
    nextSlide: 'Nākamais slaids',
    slideOf: 'Slaids {at} no {count}',
    showNotes: 'Runātāja piezīmes',
    hideNotes: 'Paslēpt runātāja piezīmes',
    notesPanel: 'Runātāja piezīmes šim slaidam',
    noNotes: 'Šim slaidam nav runātāja piezīmju.',
    presentHint: 'Bultiņas maina slaidu, N parāda piezīmes, Esc aptur.',
    moveHint: '← → lai pārvietotos · Esc lai apturētu',
    notesLabel: 'Runātāja piezīmes · tās redzat tikai jūs',
  },
  // Maltese
  mt: {
    presenting: 'Preżentazzjoni għaddejja',
    stopPresenting: 'Waqqaf il-preżentazzjoni',
    previousSlide: 'Is-slajd preċedenti',
    nextSlide: 'Is-slajd li jmiss',
    slideOf: 'Slajd {at} minn {count}',
    showNotes: 'Noti tal-kelliem',
    hideNotes: 'Aħbi n-noti tal-kelliem',
    notesPanel: 'Noti tal-kelliem għal dan is-slajd',
    noNotes: 'Dan is-slajd m’għandux noti tal-kelliem.',
    presentHint: 'Il-vleġeġ jbiddlu s-slajd, N turi n-noti, Esc iwaqqaf.',
    moveHint: '← → biex tmur ’il quddiem jew lura · Esc biex twaqqaf',
    notesLabel: 'Noti tal-kelliem · tarahom int biss',
  },
  // Dutch
  nl: {
    presenting: 'Presenteren',
    stopPresenting: 'Presentatie stoppen',
    previousSlide: 'Vorige dia',
    nextSlide: 'Volgende dia',
    slideOf: 'Dia {at} van {count}',
    showNotes: 'Sprekersnotities',
    hideNotes: 'Sprekersnotities verbergen',
    notesPanel: 'Sprekersnotities bij deze dia',
    noNotes: 'Geen sprekersnotities bij deze dia.',
    presentHint: 'Pijltjestoetsen wisselen van dia, N toont notities, Esc stopt.',
    moveHint: '← → om te bladeren · Esc om te stoppen',
    notesLabel: 'Sprekersnotities · alleen jij ziet deze',
  },
  // Polish
  pl: {
    presenting: 'Prezentowanie',
    stopPresenting: 'Zatrzymaj prezentację',
    previousSlide: 'Poprzedni slajd',
    nextSlide: 'Następny slajd',
    slideOf: 'Slajd {at} z {count}',
    showNotes: 'Notatki prelegenta',
    hideNotes: 'Ukryj notatki prelegenta',
    notesPanel: 'Notatki prelegenta do tego slajdu',
    noNotes: 'Ten slajd nie ma notatek prelegenta.',
    presentHint: 'Strzałki zmieniają slajd, N pokazuje notatki, Esc zatrzymuje.',
    moveHint: '← → aby przechodzić · Esc aby zatrzymać',
    notesLabel: 'Notatki prelegenta · widzisz je tylko ty',
  },
  // Portuguese
  pt: {
    presenting: 'A apresentar',
    stopPresenting: 'Parar a apresentação',
    previousSlide: 'Diapositivo anterior',
    nextSlide: 'Diapositivo seguinte',
    slideOf: 'Diapositivo {at} de {count}',
    showNotes: 'Notas do orador',
    hideNotes: 'Ocultar as notas do orador',
    notesPanel: 'Notas do orador deste diapositivo',
    noNotes: 'Este diapositivo não tem notas do orador.',
    presentHint: 'As setas mudam de diapositivo, N mostra as notas e Esc para.',
    moveHint: '← → para avançar ou recuar · Esc para parar',
    notesLabel: 'Notas do orador · só tu as vês',
  },
  // Romanian
  ro: {
    presenting: 'Se prezintă',
    stopPresenting: 'Oprește prezentarea',
    previousSlide: 'Diapozitivul anterior',
    nextSlide: 'Diapozitivul următor',
    slideOf: 'Diapozitivul {at} din {count}',
    showNotes: 'Notițele vorbitorului',
    hideNotes: 'Ascunde notițele vorbitorului',
    notesPanel: 'Notițele vorbitorului pentru acest diapozitiv',
    noNotes: 'Acest diapozitiv nu are notițe ale vorbitorului.',
    presentHint: 'Săgețile schimbă diapozitivul, N afișează notițele, Esc oprește.',
    moveHint: '← → pentru a naviga · Esc pentru a opri',
    notesLabel: 'Notițele vorbitorului · doar tu le vezi',
  },
  // Slovak
  sk: {
    presenting: 'Prezentuje sa',
    stopPresenting: 'Zastaviť prezentáciu',
    previousSlide: 'Predchádzajúca snímka',
    nextSlide: 'Ďalšia snímka',
    slideOf: 'Snímka {at} z {count}',
    showNotes: 'Poznámky rečníka',
    hideNotes: 'Skryť poznámky rečníka',
    notesPanel: 'Poznámky rečníka k tejto snímke',
    noNotes: 'Táto snímka nemá poznámky rečníka.',
    presentHint: 'Šípky menia snímky, N zobrazí poznámky, Esc zastaví.',
    moveHint: '← → na prechod · Esc na zastavenie',
    notesLabel: 'Poznámky rečníka · vidíte ich len vy',
  },
  // Slovenian
  sl: {
    presenting: 'Predstavitev poteka',
    stopPresenting: 'Ustavi predstavitev',
    previousSlide: 'Prejšnja prosojnica',
    nextSlide: 'Naslednja prosojnica',
    slideOf: 'Prosojnica {at} od {count}',
    showNotes: 'Zapiski govorca',
    hideNotes: 'Skrij zapiske govorca',
    notesPanel: 'Zapiski govorca za to prosojnico',
    noNotes: 'Ta prosojnica nima zapiskov govorca.',
    presentHint: 'Puščici zamenjata prosojnico, N prikaže zapiske, Esc ustavi.',
    moveHint: '← → za pomik · Esc za ustavitev',
    notesLabel: 'Zapiski govorca · vidite jih samo vi',
  },
};

const artifactViewersCopy: NamespaceCatalog<Key> = { en, sv, ...translated };

export default artifactViewersCopy;
